import { ImageOff, Save } from 'lucide-react'

import { ActionInfoQuiet, Button, ScreenInfo } from '@krp/ui-kit'

import '../../../shared/platform.css'
import { QueryState } from '../../../shared/QueryState'
import { ScanPhotoRun } from '../../scan-session/ui/ScanPhotoRun'
import type { PhotoAttachActions, PhotoAttachVm } from '../model/attach'
import './photoAttach.css'

/** Справка окна — единственная ⓘ, сверху слева. */
export const PHOTO_ATTACH_INFO = {
  title: 'Прикрепление фото',
  lines: [
    'Снимок остаётся временной загрузкой товара: пока вы не отправите его в источник, он нигде не лежит, кроме нашего хранилища, и хранится столько суток, сколько указано в настройках «Фото».',
    'Отправить фото в источник можно кнопкой в окне фото товара.',
    'Ничего не записывается, пока вы не нажмёте «Сохранить».',
    'После «Сохранить» фото показывается без фона с пунктирной рамкой — там пройдёт обрезка. Потяните рамку за угол или сторону, если режет лишнее (для точности увеличьте фото), и нажмите «Принять»; «Отмена» — не загружать.',
  ],
} as const

/**
 * Подтверждение «Для товара «…» установить это фото?» (RP-FB56, PHI-13a): миниатюра нового снимка, «Сохранить» и «Отмена». Сохранение показывает ход загрузки по реальным байтам;
 * ошибка — дословно, с кодом и «Скопировать ошибку», и «Повторить» с тем же ключом операции. Вёрстка от телефона.
 */
export function PhotoAttachScreen({ vm, actions }: { vm: PhotoAttachVm; actions: PhotoAttachActions }) {
  const saving = vm.upload !== null
  const title = vm.productName === null ? 'Установить это фото?' : `Для товара «${vm.productName}» установить это фото?`
  return (
    <ActionInfoQuiet>
      <section className="platform-attach" aria-labelledby="platform-attach-title">
        <header className="platform-attach__head">
          <ScreenInfo title={PHOTO_ATTACH_INFO.title} lines={PHOTO_ATTACH_INFO.lines} />
          <h2 id="platform-attach-title">{title}</h2>
        </header>

        {vm.product.status !== 'ready' && <QueryState query={vm.product} emptyText="Товар не найден" onRetry={actions.reload} />}

        <div className="platform-attach__pair">
          <figure className="platform-attach__figure">
            <figcaption>Новое</figcaption>
            {vm.newUrl === null ? (
              <span className="platform-attach__empty" role="img" aria-label="Снимка нет">
                <ImageOff size={28} aria-hidden="true" />
                <span>снимка нет</span>
              </span>
            ) : (
              <img src={vm.newUrl} alt="Новое фото: снимок со сканера" />
            )}
          </figure>
        </div>

        {vm.upload !== null && (
          <ScanPhotoRun
            view={vm.upload}
            onRetry={actions.retryUpload}
            onCancel={actions.cancelUpload}
            onClose={actions.dismissUpload}
            onAcceptCrop={actions.acceptCrop}
            onReload={actions.reload}
          />
        )}

        {vm.blocker !== null && !saving && <p className="platform-attach__note" role="status">{vm.blocker}</p>}

        <footer className="platform-attach__bar">
          <Button
            variant="primary"
            icon={<Save size={16} aria-hidden="true" />}
            busy={saving}
            disabled={vm.blocker !== null}
            disabledReason={vm.blocker}
            onClick={() => actions.save()}
            actionId="platform.photo-attach.save"
          >
            Сохранить
          </Button>
          <Button onClick={actions.cancel} disabled={saving} actionId="platform.photo-attach.cancel">
            Отмена
          </Button>
        </footer>
      </section>
    </ActionInfoQuiet>
  )
}
